import { useState } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle, LayoutDashboard, Store, Tag, Plus, Facebook, CreditCard, Sparkles,
  Search, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, Clock, XCircle,
  Wand2, Upload, Send, Save, Eye, Users, ArrowRight, Info, MessageCircle, ShieldCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Sections d'aide. Chaque section a un id, un titre, une icône, un contenu (jsx). */
const SECTIONS = [
  {
    id: "demarrer",
    title: "Bien démarrer",
    icon: Sparkles,
    intro: "Les 5 minutes pour découvrir votre espace pro PROXIreduc-28.",
    body: (
      <div className="space-y-3 text-slate-700">
        <p>Bienvenue dans votre <b>Espace Pro</b> ! Vous pouvez ici :</p>
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>Compléter votre mini-site local (photos, horaires, description)</li>
          <li>Publier des <b>offres</b> et bons plans destinés aux salariés/adhérents</li>
          <li>Soumettre des <b>publications Facebook</b> diffusées sur nos groupes locaux partenaires (formules Visibilité & Premium)</li>
          <li>Consulter vos statistiques et gérer votre abonnement</li>
        </ul>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm">
          <b className="text-emerald-800">Conseil :</b> commencez par compléter votre mini-site <Link to="/pro/entreprise" className="text-emerald-700 font-semibold hover:underline">Mon entreprise</Link>, puis créez votre <Link to="/pro/offres/nouvelle" className="text-emerald-700 font-semibold hover:underline">première offre</Link>.
        </div>
      </div>
    ),
  },
  {
    id: "dashboard",
    title: "Tableau de bord",
    icon: LayoutDashboard,
    intro: "Vue synthétique de votre activité, statistiques et alertes.",
    body: (
      <div className="space-y-3 text-slate-700 text-sm">
        <p>Le tableau de bord affiche en un coup d'œil vos indicateurs clés :</p>
        <ul className="grid sm:grid-cols-2 gap-2">
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Vues</b> : nombre de consultations de vos offres.</li>
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Clics</b> : interactions (téléphone, site web, itinéraire).</li>
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Utilisations</b> : bons plans consommés en magasin.</li>
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Offres actives</b> vs. <b>expirées</b>.</li>
        </ul>
        <p>Utilisez ces chiffres pour ajuster vos offres et communiquer sur celles qui marchent.</p>
      </div>
    ),
  },
  {
    id: "entreprise",
    title: "Mon entreprise",
    icon: Store,
    intro: "Vitrine de votre commerce. Complétez-la à 100% pour maximiser la visibilité.",
    body: (
      <div className="space-y-3 text-slate-700 text-sm">
        <p>Cette page rassemble toutes les informations affichées sur votre mini-site public :</p>
        <ol className="list-decimal list-inside space-y-1">
          <li><b>Nom, catégorie, ville</b> — obligatoires.</li>
          <li><b>Description</b> — présentez votre commerce en 3-4 phrases (ambiance, spécialités, différence).</li>
          <li><b>Photos</b> — 1 image de couverture minimum, jusqu'à plusieurs images pour une galerie.</li>
          <li><b>Horaires, adresse, téléphone, site web, réseaux sociaux</b>.</li>
        </ol>
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-3">
          <b className="text-blue-800">Astuce :</b> un mini-site complet est mis en avant automatiquement dans les résultats de recherche et sur la carte.
        </div>
      </div>
    ),
  },
  {
    id: "offres",
    title: "Mes offres & bons plans",
    icon: Tag,
    intro: "Créer, éditer et gérer les offres visibles par les bénéficiaires PROXIreduc-28.",
    body: (
      <div className="space-y-4 text-slate-700 text-sm">
        <div>
          <h4 className="font-bold text-slate-900 mb-2">1. Créer une offre</h4>
          <p>Cliquez sur <Link to="/pro/offres/nouvelle" className="text-[#E05638] font-semibold hover:underline"><Plus size={12} className="inline"/> Créer une offre</Link>. Renseignez :</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
            <li><b>Titre</b> accrocheur (ex : "-20% sur le menu du midi").</li>
            <li><b>Catégorie</b> (Restaurant, Beauté, Loisirs…).</li>
            <li><b>Description détaillée</b> : conditions, exclusions, comment en profiter.</li>
            <li><b>Réduction</b> : pourcentage, montant fixe, ou offre spéciale.</li>
            <li><b>Dates de validité</b> (début / fin).</li>
            <li><b>Photo</b> : mettez en avant votre produit ou votre commerce.</li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2">2. Statuts d'une offre</h4>
          <ul className="grid sm:grid-cols-2 gap-2">
            <li className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2"><Clock size={14} className="text-amber-600"/> <b>En attente</b> : soumise, en attente de validation admin.</li>
            <li className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2"><CheckCircle2 size={14} className="text-emerald-600"/> <b>Publiée</b> : visible pour les bénéficiaires.</li>
            <li className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg p-2"><Clock size={14} className="text-slate-500"/> <b>Expirée</b> : date de fin dépassée.</li>
            <li className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg p-2"><XCircle size={14} className="text-rose-600"/> <b>Refusée</b> : contenu non conforme (voir commentaire admin).</li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2">3. Bonnes pratiques</h4>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Renouvelez vos offres régulièrement — les bénéficiaires attendent des nouveautés.</li>
            <li>Précisez toujours les <b>conditions</b> (jours/horaires, quantité, cumulable ou non).</li>
            <li>Ajoutez une <b>photo de qualité</b> — les offres avec photo génèrent 3× plus de clics.</li>
            <li>Consultez régulièrement vos <b>statistiques</b> pour identifier ce qui fonctionne.</li>
          </ul>
        </div>

        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <b className="text-emerald-800">À noter :</b> aucune commission sur les ventes. Toutes les offres publiées sont gratuites, quel que soit votre plan.
        </div>
      </div>
    ),
  },
  {
    id: "publications-facebook",
    title: "Publications Facebook",
    icon: Facebook,
    intro: "Faites parler de votre commerce dans les groupes Facebook locaux partenaires (Visibilité & Premium).",
    body: (
      <div className="space-y-4 text-slate-700 text-sm">
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-3">
          <b className="text-blue-900 flex items-center gap-1"><Info size={14}/> Qui peut publier ?</b>
          <div className="text-slate-700 mt-1">
            Cette fonctionnalité est réservée aux formules :
            <ul className="list-disc list-inside mt-1 ml-2">
              <li><b>Visibilité</b> : 1 publication FB / mois</li>
              <li><b>Premium</b> : 2 publications FB / mois</li>
            </ul>
            Les formules Gratuit et Essentiel n'incluent pas ce service.
          </div>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#E05638] text-white grid place-items-center text-xs font-bold">1</span>
            Rédigez votre publication
          </h4>
          <p>Rendez-vous sur <Link to="/pro/publications-facebook" className="text-[#E05638] font-semibold hover:underline">Publications Facebook</Link> et remplissez le formulaire :</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
            <li><b>Type</b> : actualité, offre, événement, nouveau produit…</li>
            <li><b>Date souhaitée</b> (optionnelle) : date à laquelle vous voudriez voir la publication en ligne.</li>
            <li><b>Titre</b> et <b>texte de la publication</b> : rédigez comme vous souhaitez qu'ils apparaissent sur Facebook.</li>
            <li><b>Coordonnées</b> : téléphone, adresse, site web, lien de destination.</li>
            <li><b>Notes pour l'équipe</b> (optionnel) : précisions internes (groupes ciblés, contraintes…).</li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-purple-600 text-white grid place-items-center text-xs"><Wand2 size={12}/></span>
            Assistant IA (facultatif)
          </h4>
          <p>Cliquez sur <b>Assistant IA</b> puis décrivez brièvement votre publication (ex : "Offre du weekend -20% sur nos menus midi, mise en avant de la terrasse").</p>
          <p className="mt-1">L'IA rédige automatiquement un <b>titre</b>, un <b>texte</b> et propose des <b>hashtags</b> adaptés au ton demandé (chaleureux, professionnel, dynamique…). Vous restez libre de l'ajuster avant l'envoi.</p>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-emerald-600 text-white grid place-items-center text-xs"><Upload size={12}/></span>
            Ajoutez jusqu'à 2 images
          </h4>
          <p>Glissez-déposez ou cliquez sur la zone d'upload. Formats acceptés : <b>JPG, PNG, WEBP, GIF</b> (max 6 Mo).</p>
          <p className="mt-1">Les images sont <b>automatiquement compressées et optimisées</b> pour Facebook (WebP, max 1200×630 px) — plus rapide à charger sur mobile, sans perte visuelle.</p>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white grid place-items-center text-xs"><Eye size={12}/></span>
            Prévisualisation temps réel
          </h4>
          <p>À droite du formulaire (ou en bas sur mobile), une <b>prévisualisation Facebook</b> montre exactement comment votre post apparaîtra sur les groupes : avatar, texte, hashtags cliquables, images, barre de réactions.</p>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white grid place-items-center text-xs"><Send size={12}/></span>
            Envoi & validation
          </h4>
          <p>Deux options :</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
            <li><b><Save size={12} className="inline"/> Enregistrer brouillon</b> : conserve votre publication sans l'envoyer. Vous pourrez la reprendre plus tard.</li>
            <li><b><Send size={12} className="inline"/> Envoyer à l'équipe</b> : soumet la publication à l'équipe PROXIreduc-28. Un email nous est envoyé automatiquement.</li>
          </ul>
          <p className="mt-1">L'équipe valide ou demande des modifications sous <b>24-48h</b>, puis programme la publication sur les groupes Facebook concernés.</p>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2">Statuts d'une publication</h4>
          <ul className="grid sm:grid-cols-2 gap-2">
            <li className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg p-2"><Save size={14} className="text-slate-500"/> <b>Brouillon</b> — vous seul le voyez.</li>
            <li className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2"><Clock size={14} className="text-amber-600"/> <b>En attente</b> — l'équipe va valider.</li>
            <li className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg p-2"><AlertTriangle size={14} className="text-orange-600"/> <b>À modifier</b> — l'admin a laissé un commentaire.</li>
            <li className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2"><CheckCircle2 size={14} className="text-emerald-600"/> <b>Validée</b> — prête à être programmée.</li>
            <li className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2"><Clock size={14} className="text-blue-600"/> <b>Programmée</b> — date de diffusion fixée.</li>
            <li className="flex items-center gap-2 bg-emerald-600 text-white rounded-lg p-2"><CheckCircle2 size={14}/> <b>Publiée</b> — en ligne sur Facebook.</li>
            <li className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg p-2"><XCircle size={14} className="text-rose-600"/> <b>Refusée</b> — non conforme (voir motif).</li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-slate-900 mb-2">Consommation du quota</h4>
          <p>Le quota mensuel est <b>décompté uniquement à la validation</b> par l'admin. Vous pouvez donc soumettre plusieurs versions sans risque : seul un post validé/programmé/publié consomme votre quota.</p>
        </div>

        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
          <b className="text-amber-900 flex items-center gap-1"><ShieldCheck size={14}/> Contenu accepté</b>
          <ul className="list-disc list-inside mt-1 space-y-0.5 text-slate-700">
            <li>Contenu conforme aux règles Facebook et aux CGU PROXIreduc-28.</li>
            <li>Pas de contenu politique, religieux, discriminant, mensonger.</li>
            <li>Les prix, promotions et coordonnées doivent être exacts au moment de la diffusion.</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "abonnement",
    title: "Mon abonnement",
    icon: CreditCard,
    intro: "Formules, tarifs, mise à niveau et facturation.",
    body: (
      <div className="space-y-3 text-slate-700 text-sm">
        <p>PROXIreduc-28 propose 4 formules pensées pour tous les besoins :</p>
        <ul className="grid sm:grid-cols-2 gap-2">
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Gratuit</b> — Fiche commerce basique, aucune publication FB.</li>
          <li className="bg-slate-50 rounded-lg p-2 border border-slate-200"><b>Essentiel</b> — Fiche + statistiques, aucune publication FB.</li>
          <li className="bg-blue-50 rounded-lg p-2 border border-blue-200"><b>Visibilité</b> — <b>1 publication FB / mois</b> + assistant IA + upload d'images.</li>
          <li className="bg-emerald-50 rounded-lg p-2 border border-emerald-200"><b>Premium</b> — <b>2 publications FB / mois</b> + toutes les options + mise en avant.</li>
        </ul>
        <p>Rendez-vous sur <Link to="/pro/abonnement" className="text-[#E05638] font-semibold hover:underline">Mon abonnement</Link> pour changer de formule, mettre à jour votre moyen de paiement ou télécharger vos factures.</p>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <b className="text-emerald-800">Zéro commission :</b> aucun prélèvement sur vos ventes. Vous ne payez que l'abonnement mensuel.
        </div>
      </div>
    ),
  },
  {
    id: "faq",
    title: "Questions fréquentes",
    icon: HelpCircle,
    intro: "Les réponses aux 10 questions les plus posées.",
    body: (
      <div className="space-y-2 text-sm">
        {[
          { q: "Combien de temps pour valider une offre ?", a: "24-48 h ouvrées en moyenne. Vous recevez un email dès validation." },
          { q: "Puis-je modifier une offre publiée ?", a: "Oui, depuis Mes offres → clic sur l'offre → Modifier. La modification est aussi soumise à validation." },
          { q: "L'assistant IA est-il payant à l'usage ?", a: "Non, il est inclus dans les formules Visibilité et Premium. Utilisation illimitée." },
          { q: "Que se passe-t-il si Facebook refuse ma publication ?", a: "L'équipe adapte le texte/l'image pour se conformer aux règles Facebook, sans consommer votre quota." },
          { q: "Puis-je changer de formule à tout moment ?", a: "Oui, depuis Mon abonnement. Le changement prend effet immédiatement (proraté)." },
          { q: "Mes images sont-elles visibles sans mon accord ?", a: "Non. Elles ne sont diffusées qu'après validation admin et sur les groupes Facebook que vous acceptez." },
          { q: "Comment suspendre temporairement mon compte ?", a: "Contactez-nous via /contact. Nous mettons votre mini-site en pause sans supprimer vos données." },
          { q: "Le quota FB est-il reporté d'un mois sur l'autre ?", a: "Non, il se remet à zéro le 1er de chaque mois." },
          { q: "Puis-je publier sur un groupe FB spécifique ?", a: "Oui, précisez-le dans les notes à l'équipe. Sinon, l'admin choisit le groupe pertinent selon votre ville." },
          { q: "Comment supprimer définitivement mon compte ?", a: "Depuis Mon abonnement → Résilier. Vous pouvez ensuite demander la suppression RGPD via /contact." },
        ].map((it) => <FaqItem key={it.q} q={it.q} a={it.a}/>)}
      </div>
    ),
  },
  {
    id: "besoin-aide",
    title: "Besoin d'aide humaine ?",
    icon: MessageCircle,
    intro: "L'équipe est là pour vous accompagner.",
    body: (
      <div className="space-y-3 text-slate-700 text-sm">
        <p>Vous avez une question spécifique, un problème technique ou besoin d'un conseil ?</p>
        <ul className="space-y-1">
          <li>📧 Écrivez-nous via <Link to="/contact" className="text-[#E05638] font-semibold hover:underline">le formulaire de contact</Link></li>
          <li>💬 Décrivez précisément votre demande, avec captures d'écran si possible</li>
          <li>⏱️ Réponse en moins de 24 h ouvrées</li>
        </ul>
      </div>
    ),
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={()=>setOpen((v)=>!v)} className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-slate-50">
        {open ? <ChevronDown size={16} className="text-slate-500 shrink-0"/> : <ChevronRight size={16} className="text-slate-500 shrink-0"/>}
        <span className="font-semibold text-slate-900">{q}</span>
      </button>
      {open && <div className="px-4 pb-3 pl-11 text-slate-600 text-sm">{a}</div>}
    </div>
  );
}

export default function ProHelp() {
  const [q, setQ] = useState("");
  const filtered = SECTIONS.filter((s) => {
    if (!q) return true;
    const qq = q.trim().toLowerCase();
    return s.title.toLowerCase().includes(qq) || (s.intro || "").toLowerCase().includes(qq) || s.id.includes(qq);
  });

  return (
    <div data-testid="pro-help-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="pro-help-title">
            <HelpCircle className="text-[#E05638]" size={26}/> Centre d'aide
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Toutes les explications pour tirer le meilleur de votre Espace Pro.</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="mt-4 relative max-w-lg">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Rechercher une aide (ex : « quota », « IA », « offre »…)" className="pl-9 h-11" data-testid="pro-help-search"/>
      </div>

      {/* Sommaire */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="pro-help-toc">
        {SECTIONS.map((s) => {
          const Ic = s.icon;
          return (
            <a key={s.id} href={`#${s.id}`}
               className="group flex items-center gap-2 rounded-xl bg-white border border-slate-200 hover:border-[#E05638] hover:bg-orange-50/50 transition-all p-3 text-sm font-semibold text-slate-800"
               data-testid={`pro-help-tocitem-${s.id}`}>
              <span className="w-8 h-8 rounded-lg bg-orange-50 text-[#E05638] grid place-items-center shrink-0"><Ic size={16}/></span>
              <span className="line-clamp-2 leading-tight">{s.title}</span>
            </a>
          );
        })}
      </div>

      {/* Sections */}
      <div className="mt-8 space-y-8">
        {filtered.map((s) => {
          const Ic = s.icon;
          return (
            <section key={s.id} id={s.id} className="scroll-mt-24 bg-white rounded-2xl border border-slate-200 p-5 md:p-6" data-testid={`pro-help-section-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center shrink-0"><Ic size={22}/></div>
                <div className="flex-1">
                  <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">{s.title}</h2>
                  {s.intro && <p className="mt-1 text-sm text-slate-500">{s.intro}</p>}
                </div>
              </div>
              <div className="mt-4">{s.body}</div>
            </section>
          );
        })}
        {filtered.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500 text-sm">
            Aucune rubrique ne correspond à votre recherche.
          </div>
        )}
      </div>

      {/* CTA bas de page */}
      <div className="mt-10 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white p-6 md:p-8" data-testid="pro-help-cta">
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Vous êtes bloqué ?</div>
            <h3 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold">Notre équipe répond en moins de 24 h</h3>
            <p className="mt-2 text-white/80 text-sm">Écrivez-nous, on met un point d'honneur à débloquer chaque partenaire rapidement.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/contact"><Button className="bg-white text-slate-900 hover:bg-white/90" data-testid="pro-help-contact"><MessageCircle size={16} className="mr-2"/> Contactez-nous</Button></Link>
            <Link to="/pro/publications-facebook"><Button variant="outline" className="text-slate-900 hover:bg-white/90" data-testid="pro-help-fb"><Facebook size={16} className="mr-2"/> Publier maintenant <ArrowRight size={14} className="ml-1"/></Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
